import { computed, makeObservable } from 'mobx'

import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'

import documents from 'modules/documents/store/collections/documents'
import type Document from 'modules/documents/store/models/Document'
import leaves from 'modules/timeoff/store/collections/leaves'
import type Leave from 'modules/timeoff/store/models/Leave'

const getTypesLabel = (type: string) => {
  switch (type) {
    case 'medical_confirmation':
      return i18n.t('time_off.medical_leave.updates.types.medical_confirmation')
    case 'medical_discharge':
      return i18n.t('time_off.medical_leave.updates.types.medical_discharge')
    case 'medical_hospital_stay':
      return i18n.t('time_off.medical_leave.updates.types.medical_hospital_stay')
    case 'medical_leave_start':
      return i18n.t('time_off.medical_leave.updates.types.medical_leave_start')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

interface IMedicalLeaveUpdate {
  [key: string]: any
}

class MedicalLeaveUpdate extends Model<IMedicalLeaveUpdate> {
  static types = {
    medical_discharge: 'medical_discharge',
    medical_confirmation: 'medical_confirmation',
    medical_hospital_stay: 'medical_hospital_stay',
  }

  static dischargeReasons = {
    improvement: 'improvement',
    recovery_professional_capacity: 'recovery_professional_capacity',
    permanent_disability_proposal: 'permanent_disability_proposal',
    maternity_start: 'maternity_start',
    absence: 'absence',
    term_exhaustion: 'term_exhaustion',
    inss_control_12_months: 'inss_control_12_months',
    medical_discharge_in_inss: 'medical_discharge_in_inss',
    medical_inspection: 'medical_inspection',
    death: 'death',
  }

  constructor(attr: IMedicalLeaveUpdate = {}) {
    super(attr)
    makeObservable(this, {
      leave: computed,
      document: computed,
      name: computed,
      isAlta: computed,
    })
  }

  static urlRoot() {
    return '/medical_leave_updates'
  }

  get leave(): Leave {
    const leaveId = this.attributes.leave_id
    const leave = leaves.get(leaveId)

    if (!leave) {
      throw Error(`Error getting leaves with id = ${leaveId}`)
    }

    return leave
  }

  get document(): Document | null | undefined {
    return documents.find({
      id: this.attributes.document_id,
    })
  }

  get name(): string {
    const key = this.attributes.leave_update_type

    return getTypesLabel(key ?? '')
  }

  get isAlta(): boolean {
    return MedicalLeaveUpdate.types.medical_discharge === this.attributes.leave_update_type
  }
}

export default MedicalLeaveUpdate
